import { Alert, Divider, Flex, Group, Input, Modal, Radio, Select, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { formatMultilineText } from '../../../../../../../shared/utils/formatText';
import { formatDate } from '../../../../../../../shared/utils/time';
import {
  AS_CS_CHARGE_TYPE_LABELS,
  AS_CS_PROCESS_TYPE_OPTIONS,
  AS_CS_RECEIPT_STATUS_OPTIONS,
} from '../../../../../../../shared/constants/asCsOptions';
import { AsCsChargeType } from '../../../../../../../shared/types/aiAgent/asCs';
import { useGetAiAgentAsCsProcessHistoryDetails } from '../../../../../../../shared/apis/ai-agent/ascs/reactQueries';
import { useParams } from 'react-router-dom';

interface IProcessDetailModalProps {
  opened: boolean;
  selectedProcessId: number;
  loading: boolean;
  error?: boolean;
  onClose: () => void;
}

const chargeTypes: AsCsChargeType[] = [AsCsChargeType.PAID, AsCsChargeType.FREE];
export default function ProcessDetailModal(props: IProcessDetailModalProps) {
  const { opened, selectedProcessId, loading, error, onClose } = props;
  const { ascsId } = useParams();

  const { data } = useGetAiAgentAsCsProcessHistoryDetails({
    id: Number(ascsId),
    processId: selectedProcessId,
  });

  const detail = data?.result[0] ?? null;

  return (
    <Modal opened={opened} onClose={onClose} title="조치 내용 조회" size="70%" centered>
      {loading && (
        <Text ta="center" c="dimmed" py="xl">
          불러오는 중입니다.
        </Text>
      )}

      {error && (
        <Alert color="red" variant="light">
          AS/CS 정보를 불러오지 못했습니다.
        </Alert>
      )}

      {!loading && !error && !detail && (
        <Text ta="center" c="dimmed" py="xl">
          검색 결과가 없습니다.
        </Text>
      )}

      {!loading && !error && detail && (
        <Flex direction={{ base: 'column', lg: 'row' }}>
          <Stack gap="md" flex={1}>
            <Select
              readOnly
              label="진행상태"
              value={detail.processStatus ? String(detail.processStatus) : null}
              data={AS_CS_RECEIPT_STATUS_OPTIONS}
            />
            {detail.cancelReason && <TextInput readOnly label="취소 사유" value={formatMultilineText(detail.cancelReason)} />}
            <Input.Wrapper label="처리 일시">
              <Input readOnly value={formatDate(detail.processDt)} />
            </Input.Wrapper>
            <Flex gap="xs">
              <TextInput flex={1} readOnly label="처리 담당자" value={formatMultilineText(detail.processManagerName)} />
              <Select
                flex={1}
                readOnly
                label="처리 방법"
                value={detail.processType ? String(detail.processType) : null}
                data={AS_CS_PROCESS_TYPE_OPTIONS}
              />
            </Flex>
            <Radio.Group label="유상유무" value={detail.chargeType ? String(detail.chargeType) : ''}>
              <Group gap="lg" mt="xs">
                {chargeTypes.map((chargeType) => (
                  <Radio
                    key={chargeType}
                    value={chargeType}
                    label={AS_CS_CHARGE_TYPE_LABELS[chargeType]}
                    disabled={detail.chargeType !== chargeType}
                  />
                ))}
              </Group>
            </Radio.Group>
            <Textarea
              readOnly
              label="원인"
              value={formatMultilineText(detail.cause)}
              rows={10}
              minRows={10}
              maxRows={10}
              autosize={false}
            />
          </Stack>
          <Divider orientation="vertical" mx="lg" />
          <Stack gap="md" flex={1}>
            <Textarea
              readOnly
              label="조치 내용"
              value={formatMultilineText(detail.processContent)}
              rows={10}
              minRows={10}
              maxRows={10}
              autosize={false}
            />

            <Textarea
              readOnly
              label="처리 메모"
              value={formatMultilineText(detail.processNote)}
              rows={8}
              minRows={8}
              maxRows={8}
              autosize={false}
            />
          </Stack>
        </Flex>
      )}
    </Modal>
  );
}
